import React, { useContext, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Code,
  Briefcase,
  Coins,
  Users,
  MessageSquare,
  Rocket,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";

const HowThisWorks = () => {
  const { user } = useContext(UserContext);
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();

  const handleAction = (path) => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    navigate(path);
  };

  const steps = [
    {
      icon: <Code size={28} color="#c59fff" />,
      title: "1. Post Your Code Issue or Project",
      desc: "Got a bug, feature, or freelance task? Post it easily with clear details, tags, and rewards. Your issue becomes visible to the developer community instantly.",
    },
    {
      icon: <Users size={28} color="#c59fff" />,
      title: "2. Developers Engage",
      desc: "Developers explore open issues and projects, discuss solutions, and share proposals. You can chat with them directly or review their profiles before hiring.",
    },
    {
      icon: <MessageSquare size={28} color="#c59fff" />,
      title: "3. Discuss & Solve Together",
      desc: "Use built-in discussions and chat to refine solutions in real time. Collaborate efficiently and track progress in one place.",
    },
    {
      icon: <Coins size={28} color="#c59fff" />,
      title: "4. Earn & Spend Coins",
      desc: "Earn coins by solving issues or completing projects. Use coins for premium visibility, AI tools, or to post high-priority issues. (More coin features coming soon!)",
    },
    {
      icon: <Briefcase size={28} color="#c59fff" />,
      title: "5. Get Freelance or Job Opportunities",
      desc: "Build your credibility. Companies notice top contributors and offer freelance projects or referrals directly through the platform.",
    },
    {
      icon: <Rocket size={28} color="#c59fff" />,
      title: "6. Grow, Learn & Earn More",
      desc: "Track your journey, showcase your solved issues and projects, and move up the leaderboard — boosting your reputation as a problem solver.",
    },
  ];

  return (
    <Wrapper>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        How <Highlight>It Works</Highlight>
      </motion.h1>

      <Subtitle>
        A simple process that connects <Highlight>Developers</Highlight> and{" "}
        <Highlight>Companies</Highlight> to solve, earn, and grow together 🚀
      </Subtitle>

      <StepsContainer>
        {steps.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <StepCard>
              <IconCircle>{s.icon}</IconCircle>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </StepCard>
          </motion.div>
        ))}
      </StepsContainer>

      <CTASection>
        <h2>Ready to start your journey?</h2>
        <Buttons>
          <PrimaryButton onClick={() => handleAction("/post-code-issue")}>
            Post Your First Issue
          </PrimaryButton>
          <SecondaryButton onClick={() => handleAction("/signup")}>
            Join as Developer
          </SecondaryButton>
        </Buttons>
      </CTASection>
    </Wrapper>
  );
};

export default HowThisWorks;

/* ---------------- Styled Components ---------------- */

const Wrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #f4f4f4;
  padding: 100px 20px;
  text-align: center;
  min-height: 100vh;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.15rem;
  color: #d2cde9;
  max-width: 700px;
  margin: 10px auto 60px;
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1100px;
  margin: 0 auto;
`;

const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 30px 20px;
  text-align: left;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  min-height: 200px;

  &:hover {
    transform: translateY(-6px);
    border-color: #c59fff;
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  }

  h3 {
    color: #fff;
    font-size: 1.1rem;
    margin-bottom: 10px;
  }

  p {
    color: #c9c9c9;
    font-size: 0.92rem;
    line-height: 1.6;
  }
`;

const IconCircle = styled.div`
  background: rgba(155, 92, 255, 0.18);
  border-radius: 50%;
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const CTASection = styled.div`
  margin-top: 100px;
  text-align: center;

  h2 {
    font-size: 1.6rem;
    margin-bottom: 25px;
    color: #fff;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: #9b5cff;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    background: #b28dff;
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.5);
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: transparent;
  border: 1px solid #9b5cff;
  color: #c59fff;
  &:hover {
    background: #9b5cff;
    color: white;
  }
`;
